import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { RootState } from '@apps/shared/src/auth/types';
import SessionTimeout from './SessionTimeout';
import { KeepSessionAlive } from './userActions';

type StateProps = {
  timeout: string;
  isLoggedIn: boolean;
};

const mapStateToProps = ({ user }: RootState): StateProps => ({
  timeout: '840000',
  isLoggedIn: user.isLoggedIn,
});

type Props = StateProps;

// Throttle function with a trailing call to limit the frequency of API calls
// func is only called maximum of every delay ms.
// For example, if we call this at second 1, 5, 10, 15, 70, then it would actually run the function at 1, 31, and 70 if the delay was 30000
function throttleKeepAlive(func: Function, delay: number): (...args: any[]) => void {
  let lastCall = 0;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: any[] | null = null;
  return function (...args: any[]) {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      func(...args);
    } else {
      lastArgs = args;
      if (!timeoutId) {
        const remaining = delay - (now - lastCall);
        timeoutId = setTimeout(() => {
          lastCall = Date.now();
          timeoutId = null;
          if (lastArgs) {
            func(...lastArgs);
            lastArgs = null;
          }
        }, remaining);
      }
    }
  };
}

export function SessionTimeoutManager({ timeout, isLoggedIn }: Props): JSX.Element {
  const [lastInteraction, setLastInteraction] = useState<number>(Date.now());
  const [showPopup, setShowPopup] = useState<boolean>(false);

  const location = useLocation();

  useEffect(() => {
    // Throttle the KeepSessionAlive function
    const throttleKeepSessionAlive = throttleKeepAlive(() => {
      setLastInteraction(Date.now());
      KeepSessionAlive();
      localStorage.setItem('currentSessionTime', Date.now().toString());
    }, 60000);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setLastInteraction(Date.now());
        localStorage.setItem('currentSessionTime', Date.now().toString());
      }
    };

    if (!showPopup && isLoggedIn) {
      window.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('click', throttleKeepSessionAlive);
      window.addEventListener('keypress', throttleKeepSessionAlive);
      window.addEventListener('scroll', throttleKeepSessionAlive, true);
    }

    return () => {
      window.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('click', throttleKeepSessionAlive);
      window.removeEventListener('keypress', throttleKeepSessionAlive);
      window.removeEventListener('scroll', throttleKeepSessionAlive, true);
    };
  }, [showPopup, isLoggedIn]);

  useEffect(() => {
    const shouldRender =
      location.pathname === '/' ||
      location.pathname === '/login' ||
      location.pathname === '/welcome' ||
      location.pathname === '/register';

    if (isLoggedIn && !shouldRender) {
      const intervalId = setInterval(() => {
        const storedTime = localStorage.getItem('currentSessionTime');

        if (storedTime) {
          const storedTimestamp = parseInt(storedTime, 10); // Specify radix parameter
          const storedDate = new Date(storedTimestamp).getTime();
          const now = Date.now();
          const timeSinceLastInteraction = now - storedDate;

          if (timeSinceLastInteraction && timeSinceLastInteraction >= Number(timeout)) {
            setShowPopup(true);
          } else {
            setShowPopup(false);
          }
        }
      }, 1000);

      return () => {
        if (intervalId) {
          clearInterval(intervalId);
        }
      };
    }

    // Add a default return statement outside the conditional block
    return undefined;
  }, [lastInteraction, timeout, isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn) {
      setLastInteraction(Date.now());
      localStorage.setItem('currentSessionTime', Date.now().toString());
      setShowPopup(false);
    } else {
      setShowPopup(false);
    }
  }, [isLoggedIn]);

  const stayLoggedIn = () => {
    setLastInteraction(Date.now());
    setShowPopup(false);
    KeepSessionAlive();
  };

  return (
    <>
      {showPopup && (
        <SessionTimeout
          sessionExpired={showPopup}
          stayLoggedIn={stayLoggedIn}
          setShowPopup={setShowPopup}
          setLastInteraction={setLastInteraction}
        />
      )}
    </>
  );
}

export default connect(mapStateToProps)(SessionTimeoutManager);
